
import React, { useCallback, useMemo } from 'react'
import { type NodeProps, useNodeConnections, useNodesData } from '@xyflow/react'
import { VariationGenerator } from '@/components/canvas/nodes/variants/VariationGenerator'
import { type GenieNodeState, NODE_DIMENSIONS } from '@genieartinc/schemas'
import { useNodeStateChange } from '@/components/canvas/hooks/useNodeStateChange'
import { NodeHandles } from '@/components/canvas/handles/NodeHandles'
import { STANDARD_HANDLES } from '@/lib/canvas/configs/handles'
import type { HandleMeta } from '@/lib/canvas/types'
import type { AttributeCategory, ConflictRule } from '@/lib/canvas/utils/variationGenerator'

export interface VariationGeneratorNodeData extends Record<string, unknown> {
  state: GenieNodeState
  handles?: HandleMeta[]
  onHoverChange?: (hovered: boolean) => void
}

function VariationGeneratorNodeComponent({ id, data, selected }: NodeProps) {
  const nodeData = data as unknown as VariationGeneratorNodeData
  const { state: genieState, onHoverChange } = nodeData
  const onStateChange = useNodeStateChange(id)

  const handleSelect = useCallback(() => {
    // Selection handled by React Flow
  }, [])

  // Read upstream DataTable categories reactively
  const incomingConnections = useNodeConnections({ handleType: 'target' })
  const sourceNodeIds = useMemo(
    () => incomingConnections.map((c) => c.source),
    [incomingConnections],
  )
  const sourceNodesData = useNodesData(sourceNodeIds)

  const { connectedCategories, connectedConflictRules } = useMemo(() => {
    for (const nodeEntry of sourceNodesData) {
      if (!nodeEntry?.data) continue
      if (nodeEntry.type === 'DataTable') {
        const sourceState = nodeEntry.data?.state as Record<string, unknown> | undefined
        return {
          connectedCategories: (sourceState?.categories as AttributeCategory[] | undefined) ?? null,
          connectedConflictRules: (sourceState?.conflictRules as ConflictRule[] | undefined) ?? [],
        }
      }
    }
    return { connectedCategories: null, connectedConflictRules: [] }
  }, [sourceNodesData])

  const generatorProps = useMemo(
    () => ({
      id,
      title: genieState.title,
      selected: selected || genieState.selected,
      width: NODE_DIMENSIONS.VariationGenerator.width,
      height: NODE_DIMENSIONS.VariationGenerator.height,
      onSelect: handleSelect,
      onHoverChange,
      onStateChange,
      state: genieState,
      connectedCategories,
      connectedConflictRules,
    }),
    [id, genieState, selected, handleSelect, onHoverChange, onStateChange, connectedCategories, connectedConflictRules],
  )

  const handles: HandleMeta[] = nodeData.handles || STANDARD_HANDLES.VariationGenerator

  return (
    <>
      <NodeHandles nodeId={id} handles={handles} />
      <VariationGenerator {...generatorProps} />
    </>
  )
}

export const VariationGeneratorNode = React.memo(
  VariationGeneratorNodeComponent,
  (prevProps, nextProps) => {
    const prevData = prevProps.data as unknown as VariationGeneratorNodeData
    const nextData = nextProps.data as unknown as VariationGeneratorNodeData

    return (
      prevData.state.title === nextData.state.title &&
      prevData.state.variationCount === nextData.state.variationCount &&
      prevData.state.generationStatus === nextData.state.generationStatus &&
      prevData.state.generationStats === nextData.state.generationStats &&
      prevData.state.count === nextData.state.count &&
      prevData.state.collapsed === nextData.state.collapsed &&
      prevData.handles === nextData.handles &&
      prevProps.selected === nextProps.selected &&
      prevProps.id === nextProps.id
    )
  },
)

VariationGeneratorNode.displayName = 'VariationGeneratorNode'
